import { useState } from 'preact/hooks';
import { useStore } from '../hooks';
import { Avatar, Field } from '../components/ui';
import { updateMember, createMember } from '../lib/actions';
import { fullResync, sync } from '../sync/engine';
import { toast } from '../components/toast';
import type { Member } from '../../shared/types';
import { apiJson, inviteLink } from '../lib/http';
import { isLocalCover } from '../lib/covers';
import { coverRunner } from '../lib/coverRunner';
import { useCoverRunner } from '../hooks';
import { useRef } from 'preact/hooks';

const EMOJIS = ['📚', '🦉', '🦊', '🌸', '☕', '🚀', '🐱', '🌿', '⭐', '🎨', '🧭', '🐻'];

export function Settings() {
  const s = useStore();
  const me = s.me;
  if (!me) return <div class="p-6 text-ink-500">Se încarcă…</div>;
  const isAdmin = me.role === 'admin';
  const members = [...s.view.members.values()].sort((a, b) => a.name.localeCompare(b.name, 'ro'));

  return (
    <div class="px-4 pb-28 pt-4 space-y-6 max-w-2xl mx-auto">
      <ProfileCard me={s.view.members.get(me.id) || me} />
      <DeviceCard />
      {isAdmin && <MembersCard members={members} />}
      {isAdmin && <InvitesCard members={members} />}
      {isAdmin && <CoversCard />}
      <SyncCard />
      {isAdmin && <ImportCard />}
      <p class="text-center text-xs text-ink-400">Casa Cărților v10 · conectat ca {me.email || me.name}</p>
    </div>
  );
}

function ProfileCard({ me }: { me: Member }) {
  const [name, setName] = useState(me.name);
  const [goal, setGoal] = useState(String(me.reading_goal || 25));
  const save = async (fields: Partial<Member>) => { await updateMember(me.id, fields); toast('Profil salvat'); };
  return (
    <section class="card p-4 space-y-4">
      <div class="flex items-center gap-3"><Avatar member={me} size={48} /><div><div class="font-serif text-xl font-bold">{me.name}</div><div class="text-xs text-ink-500">{me.role === 'admin' ? 'Administrator' : me.role === 'child' ? 'Copil' : 'Membru'}</div></div></div>
      <Field label="Numele afișat"><input class="field" value={name} onInput={e => setName((e.target as HTMLInputElement).value)} onBlur={() => name.trim() && name !== me.name && save({ name: name.trim() })} /></Field>
      <div>
        <span class="label">Avatar</span>
        <div class="flex flex-wrap gap-2">{EMOJIS.map(e => <button key={e} class={`h-10 w-10 rounded-full text-xl ${me.avatar === e ? 'ring-2 ring-terra-500 bg-terra-50' : 'bg-cream-200'}`} onClick={() => save({ avatar: e })}>{e}</button>)}</div>
      </div>
      <Field label="Obiectiv de lectură pe an"><input class="field" inputMode="numeric" value={goal} onInput={e => setGoal((e.target as HTMLInputElement).value)} onBlur={() => Number(goal) > 0 && Number(goal) !== me.reading_goal && save({ reading_goal: Number(goal) })} /></Field>
    </section>
  );
}

function MembersCard({ members }: { members: Member[] }) {
  const [newName, setNewName] = useState('');
  return (
    <section class="card p-4">
      <div class="font-serif text-lg font-bold">Membrii familiei</div>
      <p class="text-xs text-ink-500 mb-3">Rolul decide ce poate face fiecare. Emailul e opțional (doar pentru un eventual login Cloudflare Access pe viitor). Accesul se dă din „Invitații”, mai jos.</p>
      <ul class="divide-y divide-cream-300">
        {members.map(m => <MemberRow key={m.id} m={m} />)}
      </ul>
      <div class="mt-3 flex gap-2">
        <input class="field" placeholder="Nume membru nou" value={newName} onInput={e => setNewName((e.target as HTMLInputElement).value)} />
        <button class="btn-primary" onClick={async () => { if (!newName.trim()) return; await createMember({ name: newName.trim(), role: 'adult', avatar: '📚' }); setNewName(''); toast('Membru adăugat'); }}>Adaugă</button>
      </div>
    </section>
  );
}

function MemberRow({ m }: { m: Member }) {
  const [email, setEmail] = useState(m.email || '');
  return (
    <li class="py-3 flex flex-wrap items-center gap-2">
      <Avatar member={m} size={32} />
      <div class="font-semibold w-24 truncate">{m.name}</div>
      <input class="field !py-1.5 text-sm flex-1 min-w-[180px]" type="email" placeholder="email@exemplu.ro" value={email}
        onInput={e => setEmail((e.target as HTMLInputElement).value)}
        onBlur={() => { if ((email || null) !== (m.email || null)) void updateMember(m.id, { email: email.trim().toLowerCase() || null }); }} />
      <select class="field !w-auto !py-1.5 text-sm" value={m.role} onChange={e => updateMember(m.id, { role: (e.target as HTMLSelectElement).value as any })}>
        <option value="admin">Admin</option><option value="adult">Adult</option><option value="child">Copil</option>
      </select>
    </li>
  );
}

function SyncCard() {
  const s = useStore();
  const st = s.status;
  const label = { idle: 'Sincronizat', syncing: 'Se sincronizează…', offline: 'Offline', error: 'Eroare', unauthorized: 'Neautentificat' }[st.state];
  return (
    <section class="card p-4 space-y-3">
      <div class="font-serif text-lg font-bold">Sincronizare</div>
      <div class="text-sm" data-testid="sync-state">{label}{st.pending ? ` · ${st.pending} modificări de trimis` : ''}{st.lastOk ? ` · ultima: ${new Date(st.lastOk).toLocaleTimeString('ro-RO')}` : ''}</div>
      {st.message && <div class="text-xs text-red-700">{st.message}</div>}
      <div class="flex gap-2">
        <button class="btn-ghost" onClick={() => sync()}>Sincronizează acum</button>
        <button class="btn-ghost" onClick={async () => { await fullResync(); toast('Biblioteca a fost reîncărcată complet de pe server'); }}>Reîncarcă tot</button>
      </div>
    </section>
  );
}

function ImportCard() {
  const [url, setUrl] = useState('https://casa-cartilor.pages.dev/api/sync');
  const [legacyName, setLegacyName] = useState('BOGDAN');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string>('');
  const run = async () => {
    setBusy(true); setResult('');
    try {
      const r = await apiJson('/api/admin/import-legacy', { method: 'POST', body: JSON.stringify({ source_url: url, me_legacy_name: legacyName }) });
      setResult(`Importate: ${r.summary.works} cărți, ${r.summary.copies} exemplare, ${r.summary.reading} lecturi, ${r.summary.members} membri noi, ${r.summary.loans} împrumuturi.`);
      await sync();
    } catch (e) { setResult('Eroare: ' + (e as Error).message); }
    setBusy(false);
  };
  return (
    <section class="card p-4 space-y-3">
      <div class="font-serif text-lg font-bold">Import din aplicația veche</div>
      <p class="text-xs text-ink-500">Se poate rula de mai multe ori fără dubluri. <b>Atenție:</b> reimportul suprascrie rafturile și statusurile cărților importate cu cele din aplicația veche, așa că după ce familia trece pe v10 nu-l mai rula.</p>
      <Field label="Sursa (v8/v9)"><input class="field" value={url} onInput={e => setUrl((e.target as HTMLInputElement).value)} /></Field>
      <Field label="Numele meu în aplicația veche"><input class="field" value={legacyName} onInput={e => setLegacyName((e.target as HTMLInputElement).value)} /></Field>
      <button class="btn-primary" disabled={busy} onClick={run}>{busy ? 'Se importă…' : 'Importă'}</button>
      {result && <div class="text-sm">{result}</div>}
    </section>
  );
}

function ShareLink({ link, who, onClose }: { link: string; who: string; onClose: () => void }) {
  const text = `Bună, ${who}! Acesta e linkul tău personal pentru Casa Cărților (biblioteca familiei). Deschide-l pe telefon și apoi „Adaugă pe ecranul de pornire”. Nu-l trimite altcuiva: ${link}`;
  return (
    <div class="mt-3 rounded-xl border border-terra-200 bg-terra-50 p-3 space-y-2" data-testid="invite-link">
      <div class="text-sm font-semibold">Link pentru {who}</div>
      <input class="field !py-1.5 text-xs font-mono" readOnly value={link} onFocus={e => (e.target as HTMLInputElement).select()} />
      <div class="flex flex-wrap gap-2">
        <button class="btn-ghost !py-1.5 text-xs" onClick={async () => { try { await navigator.clipboard.writeText(link); toast('Link copiat'); } catch { toast('Selectează și copiază manual linkul', 'error'); } }}>Copiază</button>
        <a class="btn-ghost !py-1.5 text-xs" href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener">Trimite pe WhatsApp</a>
        <button class="btn-ghost !py-1.5 text-xs" onClick={onClose}>Gata</button>
      </div>
      <p class="text-[11px] text-ink-500">Linkul funcționează până îl revoci. Oricine îl are intră ca {who}, deci trimite-l doar persoanei respective.</p>
    </div>
  );
}

function DeviceCard() {
  const [link, setLink] = useState('');
  const create = async () => {
    try { const r = await apiJson('/api/invites/self', { method: 'POST', body: JSON.stringify({ label: 'alt dispozitiv' }) }); setLink(inviteLink(r.token)); }
    catch (e) { toast((e as Error).message, 'error'); }
  };
  return (
    <section class="card p-4">
      <div class="font-serif text-lg font-bold">Alt dispozitiv al meu</div>
      <p class="text-xs text-ink-500 mb-3">Vrei aplicația și pe PC sau pe tabletă? Generează un link și deschide-l acolo.</p>
      {!link ? <button class="btn-ghost" onClick={create}>Generează link pentru alt dispozitiv</button> : <ShareLink link={link} who="tine" onClose={() => setLink('')} />}
    </section>
  );
}

function InvitesCard({ members }: { members: Member[] }) {
  const [memberId, setMemberId] = useState('');
  const [link, setLink] = useState<{ link: string; who: string } | null>(null);
  const [list, setList] = useState<any[] | null>(null);
  const load = async () => { try { setList((await apiJson('/api/admin/invites')).invites); } catch (e) { toast((e as Error).message, 'error'); } };
  const create = async () => {
    if (!memberId) return;
    try {
      const r = await apiJson('/api/admin/invites', { method: 'POST', body: JSON.stringify({ member_id: memberId, label: 'invitație' }) });
      setLink({ link: inviteLink(r.token), who: r.member }); if (list) void load();
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const revoke = async (id: string) => {
    if (!confirm('Revoci accesul acestui dispozitiv?')) return;
    await apiJson(`/api/admin/invites/${id}/revoke`, { method: 'POST' }); toast('Acces revocat'); void load();
  };
  return (
    <section class="card p-4 space-y-3">
      <div class="font-serif text-lg font-bold">Invitații</div>
      <p class="text-xs text-ink-500">Fiecare membru primește un link personal. Îl deschide o dată pe telefon și rămâne conectat, fără parole și fără conturi.</p>
      <div class="flex gap-2">
        <select class="field" value={memberId} onChange={e => setMemberId((e.target as HTMLSelectElement).value)} aria-label="Membru">
          <option value="">Alege membrul…</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <button class="btn-primary whitespace-nowrap" disabled={!memberId} onClick={create}>Creează link</button>
      </div>
      {link && <ShareLink link={link.link} who={link.who} onClose={() => setLink(null)} />}
      <button class="text-sm font-semibold text-terra-600" onClick={load}>{list ? 'Reîmprospătează' : 'Vezi dispozitivele conectate'}</button>
      {list && (
        <ul class="divide-y divide-cream-300 text-sm">
          {list.length === 0 && <li class="py-2 text-ink-500">Niciun dispozitiv.</li>}
          {list.map(t => (
            <li key={t.id} class="py-2 flex items-center gap-2">
              <div class="flex-1 min-w-0">
                <div class="font-semibold">{t.member} <span class="font-normal text-ink-500">· {t.label || 'link'}</span></div>
                <div class="text-xs text-ink-500">creat {t.created_at}{t.last_used_at ? ` · folosit ${t.last_used_at}` : ' · nefolosit încă'}{t.revoked_at ? ' · REVOCAT' : ''}</div>
              </div>
              {!t.revoked_at && <button class="btn-danger !py-1 !px-2 text-xs" onClick={() => revoke(t.id)}>Revocă</button>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Coperți automate pentru toată biblioteca: aduce coperțile vechi „acasă” (în D1) și caută coperți pentru cărțile fără.
function CoversCard() {
  const s = useStore();
  const run = useCoverRunner();
  const works = [...s.view.works.values()];
  const external = works.filter(w => w.cover_url && !isLocalCover(w.cover_url));
  const missing = works.filter(w => !w.cover_url);
  const toReview = works.filter(w => w.cover_source === 'auto').length;

  const start = async () => {
    const r = await coverRunner.start([...external, ...missing], { replace: false, label: 'Toată biblioteca' });
    if (r) toast(r.stopped ? 'Oprit.' : `Gata: ${r.ok} coperți salvate, ${r.fail} negăsite.`);
  };
  const review = () => { sessionStorage.setItem('cc10_cover_review', '1'); window.dispatchEvent(new CustomEvent('cc10:goto', { detail: 'mine' })); };

  return (
    <section class="card p-4 space-y-3">
      <div class="font-serif text-lg font-bold">Coperți automate</div>
      <p class="text-xs text-ink-500">Caută automat coperta fiecărei cărți (okazii, librării românești, Google Books, Open Library), alege prima potrivită și o salvează în aplicație. Tu verifici doar ce e greșit. Pentru doar câteva cărți: <b>Editare în grup → 🖼 Coperți</b>.</p>
      <div class="grid grid-cols-3 gap-2 text-center text-xs">
        <div class="rounded-xl bg-cream-200 p-2"><div class="text-lg font-bold text-ink-900">{missing.length}</div>fără copertă</div>
        <div class="rounded-xl bg-cream-200 p-2"><div class="text-lg font-bold text-ink-900">{external.length}</div>cu link extern</div>
        <div class="rounded-xl bg-cream-200 p-2"><div class="text-lg font-bold text-ink-900">{toReview}</div>de verificat</div>
      </div>
      <div class="flex flex-wrap gap-2">
        {run.running
          ? <button class="btn-ghost" onClick={() => coverRunner.stop()}>Oprește</button>
          : <button class="btn-primary" disabled={external.length + missing.length === 0} onClick={start}>✨ Caută coperțile automat</button>}
        {toReview > 0 && <button class="btn-ghost" onClick={review}>Verifică {toReview} coperți</button>}
      </div>
      <p class="text-[11px] text-ink-500">Ține aplicația deschisă cât rulează (≈ 2–4 secunde pe carte). Progresul apare jos, pe orice ecran.</p>
    </section>
  );
}
